let popupHost = null;
let nativeAlert = null;

const DEFAULT_LABELS = {
  alert: "Got it",
  confirm: "Confirm",
  cancel: "Cancel",
};

function cleanupMessage(message = "") {
  return String(message || "")
    .replace(/[✅❌⚠️]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function inferVariant(text = "", fallback = "info") {
  const value = cleanupMessage(text).toLowerCase();
  if (!value) return fallback;
  if (
    /(success|saved|created|updated|resent|sent|processed|credited|applied|executed|completed successfully)/i.test(
      value,
    )
  )
    return "success";
  if (
    /(warning|archive|delete|remove|reset|re-import|cannot be undone|are you sure|completed\?|reverse|deactivate)/i.test(
      value,
    )
  )
    return "warning";
  if (
    /(fail|failed|error|invalid|expired|required|missing|unable|not found|limit reached|please fix|must be|select|choose|enter valid)/i.test(
      value,
    )
  )
    return "error";
  return fallback;
}

function inferTitle(kind, message, explicitTitle, variant) {
  if (explicitTitle) return explicitTitle;

  const normalized = String(message || "").trim();
  if (normalized.includes("\n\n")) {
    return normalized.split(/\n\n/)[0].trim();
  }

  if (kind === "confirm") {
    if (/(delete|remove|reset|re-import|cannot be undone)/i.test(normalized))
      return "Confirm Destructive Action";
    if (
      /(archive|activate|execute|send|resend|mark|reverse|trigger|complete)/i.test(
        normalized,
      )
    )
      return "Please Confirm";
    return "Confirm Action";
  }

  if (variant === "success") return "Success";
  if (variant === "error") return "Something needs attention";
  if (variant === "warning") return "Please review";
  return "Information";
}

function inferConfirmLabel(message, explicitLabel) {
  if (explicitLabel) return explicitLabel;
  const normalized = String(message || "").toLowerCase();
  if (normalized.includes("delete")) return "Delete";
  if (normalized.includes("archive")) return "Archive";
  if (normalized.includes("activate")) return "Activate";
  if (normalized.includes("execute")) return "Execute";
  if (normalized.includes("resend") || normalized.includes("send"))
    return "Send";
  if (normalized.includes("trigger")) return "Trigger";
  if (normalized.includes("remove")) return "Remove";
  if (normalized.includes("complete")) return "Complete Session";
  if (normalized.includes("mark")) return "Continue";
  return DEFAULT_LABELS.confirm;
}

function normalizeMessage(input) {
  if (typeof input === "string") {
    const parts = input.split(/\n\n/);
    if (parts.length > 1) {
      return {
        title: parts[0].trim(),
        message: parts.slice(1).join("\n\n").trim(),
      };
    }
    return { message: input };
  }

  return {
    title: input?.title,
    message: input?.message || "",
    details: input?.details,
    confirmLabel: input?.confirmLabel,
    cancelLabel: input?.cancelLabel,
    variant: input?.variant,
  };
}

function ensureHost() {
  return popupHost;
}

function fallbackAlert(message) {
  if (nativeAlert) {
    nativeAlert(message);
  }
}

export function registerPopupHost(host) {
  popupHost = host;
}

export function unregisterPopupHost() {
  popupHost = null;
}

export function showAlert(input, overrides = {}) {
  const base = normalizeMessage(input);
  const merged = { ...base, ...overrides };
  const variant =
    merged.variant ||
    inferVariant(`${merged.title || ""} ${merged.message || ""}`, "info");
  const payload = {
    kind: "alert",
    variant,
    title: inferTitle("alert", merged.message, merged.title, variant),
    message: merged.message || "",
    details: merged.details,
    confirmLabel: merged.confirmLabel || DEFAULT_LABELS.alert,
  };

  const host = ensureHost();
  if (!host) {
    fallbackAlert(
      [payload.title, payload.message].filter(Boolean).join("\n\n"),
    );
    return Promise.resolve();
  }

  return host.open(payload);
}

export function showConfirm(input, overrides = {}) {
  const base = normalizeMessage(input);
  const merged = { ...base, ...overrides };
  const variant =
    merged.variant ||
    inferVariant(`${merged.title || ""} ${merged.message || ""}`, "warning");
  const payload = {
    kind: "confirm",
    variant,
    title: inferTitle("confirm", merged.message, merged.title, variant),
    message: merged.message || "",
    details: merged.details,
    confirmLabel: inferConfirmLabel(merged.message, merged.confirmLabel),
    cancelLabel: merged.cancelLabel || DEFAULT_LABELS.cancel,
  };

  const host = ensureHost();
  if (!host) {
    return Promise.resolve(window.confirm(payload.message || payload.title));
  }

  return host.open(payload);
}

/** Route all alert() calls through the themed popup. Use showConfirm() for confirmations. */
export function installPopupOverrides() {
  if (typeof window === "undefined") return;
  if (!nativeAlert) nativeAlert = window.alert.bind(window);

  window.alert = (input) => {
    showAlert(typeof input === "string" ? input : String(input ?? ""));
  };
}
